// openEHR RM model — BMM package org.openehr.rm.demographic
// Generated by tasks/split_model_by_bmm_packages.ts from tasks/bmm_package_map.json.
// Package boundaries follow the BMM package structure; edit the class bodies here,
// but re-run the splitter rather than hand-moving declarations between packages.

import type { T } from "../_shared.ts";
import { LOCATABLE } from "../common/archetyped.ts";
import { VERSIONED_OBJECT } from "../common/change_control.ts";
import type { ITEM_STRUCTURE } from "../data_structures/item_structure.ts";
import type { DV_TEXT } from "../data_types/text.ts";
import * as openehr_base from "../../base/mod.ts";
import { applyRmInit, type RmInit } from "../../base/apply_rm_init.ts";

/**
 * Ancestor of all Party types, including real world entities and their roles. A Party is any entity which can participate in an activity. The \`_name_\` attribute inherited from \`LOCATABLE\` is used to indicate the actual type of party (note that the actual names, i.e. identities of parties are indicated in the \`_identities_\` attribute, not the \`_name_\` attribute).
 *
 * NOTE: It is strongly recommended that the inherited attribute \`_uid_\` be populated in \`PARTY\` objects, using the UID copied from the \`_object_id()_\` of the \`_uid_\` field of the enclosing \`VERSION\` object. +
 * For example, the \`ORIGINAL_VERSION.uid\` \`87284370-2D4B-4e3d-A3F3-F303D2F4F34B::uk.nhs.ehr1::2\`  would be copied to the \`_uid_\` field of the \`PARTY\` object.
 */
export abstract class PARTY extends LOCATABLE {
  /**
   * Identities used by the party to identify itself, such as legal name, stage names, aliases, nicknames and so on.
   */
  identities?: undefined;
  /**
   * Contacts for this party.
   */
  contacts?: undefined;
  /**
   * All other details for this Party.
   */
  details?: ITEM_STRUCTURE;
  /**
   * References to relationships in which this Party takes part as target.
   */
  reverse_relationships?: undefined;
  /**
   * Relationships in which this Party takes part as source.
   */
  relationships?: undefined;
  /**
   * Type of party, such as  \`PERSON\`,  \`ORGANISATION\`, etc. Role name, e.g.  general practitioner ,  nurse ,  private citizen . Taken from inherited \`_name_\` attribute.
   * @returns Result value
   */
  type(): DV_TEXT {
    // Return the name attribute which contains the type/role
    if (!this.name) {
      throw new Error("PARTY name is required but not set");
    }
    return this.name;
  }
}

/**
 * Description of a means of contact of a Party. Actual structure is archetyped.
 */
export class CONTACT extends LOCATABLE {
  constructor(init?: RmInit) { super(); applyRmInit(this, init); }

  /**
   * A set of address alternatives for this contact purpose and time validity combination.
   */
  addresses?: undefined;
  /**
   * Valid time interval for this contact descriptor.
   */
  time_validity?: undefined;
  /**
   * Purpose for which this contact is used, e.g. mail,  daytime phone, etc. Taken from value of inherited \`_name_\` attribute.
   * @returns Result value
   */
  purpose(): DV_TEXT {
    // Return the name attribute which contains the purpose
    if (!this.name) {
      throw new Error("CONTACT name is required but not set");
    }
    return this.name;
  }
}

/**
 * Address of contact, which may be electronic or geographic.
 */
export class ADDRESS extends LOCATABLE {
  constructor(init?: RmInit) { super(); applyRmInit(this, init); }

  /**
   * Archetypable structured address.
   */
  details?: ITEM_STRUCTURE;
  /**
   * Type of address, e.g. electronic,  locality. Taken from value of inherited \`_name_\` attribute.
   * @returns Result value
   */
  type(): DV_TEXT {
    // Return the name attribute which contains the type
    if (!this.name) {
      throw new Error("ADDRESS name is required but not set");
    }
    return this.name;
  }
}

/**
 * An identity  owned  by a Party, such as a person name or company name, and which is used by the Party to identify itself. Actual structure is archetyped.
 */
export class PARTY_IDENTITY extends LOCATABLE {
  constructor(init?: RmInit) { super(); applyRmInit(this, init); }

  /**
   * The value of the identity. This will often taken the form of a parseable string or a small structure of strings.
   */
  details?: ITEM_STRUCTURE;
  /**
   * Purpose of identity, e.g. legal ,  stagename,  nickname,  tribal name,  trading name. Taken from value of inherited \`_name_\` attribute.
   * @returns Result value
   */
  purpose(): DV_TEXT {
    // Return the name attribute which contains the purpose
    if (!this.name) {
      throw new Error("PARTY_IDENTITY name is required but not set");
    }
    return this.name;
  }
}

/**
 * Generic description of a role performed by an Actor. The role corresponds to a competency of the Party. Roles are used to define the responsibilities undertaken by a Party for a purpose. Roles should have credentials qualifying the performer to perform the role.
 */
export class ROLE extends PARTY {
  constructor(init?: RmInit) { super(); applyRmInit(this, init); }

  /**
   * Valid time interval for this role.
   */
  time_validity?: undefined;
  /**
   * Reference to Version container of Actor playing the role.
   */
  performer?: openehr_base.PARTY_REF;
  /**
   * The capabilities of this role.
   */
  capabilities?: undefined;
}

/**
 * Ancestor of all real-world types, including people and organisations. An actor is any real-world entity capable of taking on a role.
 */
export abstract class ACTOR extends PARTY {
  /**
   * Languages which can be used to communicate with this actor, in preferred order of use (if known, else order irrelevant).
   */
  languages?: undefined;
  /**
   * Identifiers of the Version container for each Role played by this Party.
   */
  roles?: undefined;
}

/**
 * Capability of a role, such as  ehr modifier,  health care provider. Capability should be backed up by credentials.
 */
export class CAPABILITY extends LOCATABLE {
  constructor(init?: RmInit) { super(); applyRmInit(this, init); }

  /**
   * The qualifications of the performer of the role for this capability. This might include professional qualifications and official identifications such as provider numbers etc.
   */
  credentials?: ITEM_STRUCTURE;
  /**
   * Valid time interval for the credentials of this capability.
   */
  time_validity?: undefined;
}

/**
 * Generic concept of any kind of agent, including devices, software systems, but not humans or organisations.
 */
export class AGENT extends ACTOR {
  constructor(init?: RmInit) { super(); applyRmInit(this, init); }

}

/**
 * Generic description of organisations. An organisation is a legally constituted body whose existence (in general) outlives the existence of parties considered to be part of it.
 */
export class ORGANISATION extends ACTOR {
  constructor(init?: RmInit) { super(); applyRmInit(this, init); }

}

/**
 * A group is a real world group of parties which is created by another party, usually an organisation, for some specific purpose. A typical clinical example is that of the specialist care team, e.g.  cardiology team . The members of the group usually work together.
 */
export class GROUP extends ACTOR {
  constructor(init?: RmInit) { super(); applyRmInit(this, init); }

}

/**
 * Generic description of persons. Provides a dedicated type to which Person archetypes can be targeted.
 */
export class PERSON extends ACTOR {
  constructor(init?: RmInit) { super(); applyRmInit(this, init); }

}

/**
 * Generic description of a relationship between parties.
 */
export class PARTY_RELATIONSHIP extends LOCATABLE {
  constructor(init?: RmInit) { super(); applyRmInit(this, init); }

  /**
   * The detailed description of the relationship.
   */
  details?: ITEM_STRUCTURE;
  /**
   * Target of relationship.
   */
  target?: openehr_base.PARTY_REF;
  /**
   * Valid time interval for this relationship.
   */
  time_validity?: undefined;
  /**
   * Source of relationship.
   */
  source?: openehr_base.PARTY_REF;
  /**
   * Type of relationship, such as  employment,  authority,  health provision
   * @returns Result value
   */
  type(): DV_TEXT {
    // Return the name attribute which contains the relationship type
    if (!this.name) {
      throw new Error("PARTY_RELATIONSHIP name is required but not set");
    }
    return this.name;
  }
}

/**
 * Static type formed by binding generic parameter of \`VERSIONED_OBJECT<T>\` to \`PARTY\`.
 */
export class VERSIONED_PARTY extends VERSIONED_OBJECT<T> {
  constructor(init?: RmInit) { super(); applyRmInit(this, init); }

}
